import type { FormSchema, FormSchemaResolved, ResolvableFormSchema } from '#layers/block-engine/types/form-schema';
import { set } from '#layers/base/utils/object';

type ResolvableFormSchemaResult<
	TResult extends object,
	TContext,
> = ReturnType<ResolvableFormSchema<TResult, TContext>>;

/**
 * Define a form schema while preserving its generic value and context types.
 */
export function defineFormSchema<
	TResult extends object = Record<string, unknown>,
	TContext = unknown,
>(schema: FormSchema<TResult, TContext>) {
	return schema;
}

export interface ResolveFormSchemaOptions<
	TResult extends object,
	TContext = unknown,
> {
	/** Schema or schema factory to resolve. */
	schema: FormSchema<TResult, TContext>;
	/** Current root form values. */
	values: TResult;
	/**
	 * Context passed to schema getter function. Required when schema is a function.
	 */
	context?: TContext;
	/** Optional post-resolve schema transform. */
	transformSchema?: (schema: FormSchemaResolved<TResult>) => FormSchemaResolved<TResult>;
}

/**
 * Check whether a form schema needs runtime resolution.
 */
function isResolvableSchema<
	TResult extends object,
	TContext,
>(schema: FormSchema<TResult, TContext>): schema is ResolvableFormSchema<TResult, TContext> {
	return typeof schema === 'function';
}

/**
 * Resolve static and callback-backed form schemas into field arrays.
 */
export function resolveFormSchema<
	TResult extends object = Record<string, unknown>,
	TContext = unknown,
>(options: ResolveFormSchemaOptions<TResult, TContext>) {
	const resolved = isResolvableSchema(options.schema)
		? options.schema({
				form: (schema) => schema as ResolvableFormSchemaResult<TResult, TContext>,
				values: options.values,
				context: options.context as TContext,
			})
		: options.schema;

	if (!options.transformSchema) {
		return resolved;
	}

	return options.transformSchema(resolved);
}

export interface GetDefaultValuesFromFormSchemaOptions<
	TResult extends object = Record<string, unknown>,
	TContext = unknown,
> {
	/** Schema to read default values from. */
	schema: FormSchema<TResult, TContext>;
	/**
	 * Optional context to pass to the schema if it's a function
	 */
	context?: TContext;
	/**
	 * Called when a field default throws. When omitted, default errors keep bubbling.
	 */
	onDefaultError?: (error: unknown, field: FormSchemaResolved<TResult>[number]) => void;
}

/**
 * Get an object with default values from a form schema.
 */
export function getDefaultValuesFromFormSchema<
	TResult extends object = Record<string, unknown>,
	TContext = unknown,
>(options: GetDefaultValuesFromFormSchemaOptions<TResult, TContext>) {
	const result: Record<string, unknown> = {};

	const resolvedSchema = resolveFormSchema({
		schema: options.schema,
		values: {} as TResult,
		context: (options.context ?? {}) as TContext,
	});

	/**
	 * Collect defaults from a possibly grouped field list.
	 */
	function processFields(fields: FormSchemaResolved<TResult>) {
		const currentResult: Record<string, unknown> = {};

		for (const field of fields) {
			if ('fields' in field && field.type === 'group') {
				Object.assign(currentResult, processFields(field.fields));
			}
			else if (field.type === 'separator') {
				continue;
			}
			else {
				if (field.default !== undefined) {
					try {
						set(currentResult, field.name, field.default());
					}
					catch (error) {
						if (!options.onDefaultError) {
							throw error;
						}

						options.onDefaultError(error, field);
						set(currentResult, field.name, undefined);
					}
				}
				else {
					set(currentResult, field.name, undefined);
				}
			}
		}

		return currentResult;
	}

	Object.assign(result, processFields(resolvedSchema));

	return result;
}
